/**
 * Phase 14.7 — the second genuine "driver must act" case: the device is nearly
 * out of storage, so future photos may fail to persist locally.
 *
 * Passive observation of the Storage API only. It never touches the upload
 * engine, the queues, IndexedDB contents or storage paths, and it warns at most
 * once per app session.
 */
import { toast } from "sonner";

const CRITICAL_FREE_BYTES = 60 * 1024 * 1024; // 60 MB
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

let warned = false;

async function checkOnce(): Promise<void> {
  if (warned) return;
  try {
    if (typeof navigator === "undefined" || !navigator.storage?.estimate) return;
    const { quota, usage } = await navigator.storage.estimate();
    if (typeof quota !== "number" || typeof usage !== "number" || quota <= 0) return;
    const free = quota - usage;
    if (free > CRITICAL_FREE_BYTES) return;
    warned = true;
    toast.warning("Storage almost full", {
      description:
        "This device is running out of space. Free some up so new photos can be saved.",
      duration: 12000,
    });
  } catch {
    /* storage estimates are advisory only */
  }
}

export function startDeviceStorageGuard(): () => void {
  void checkOnce();
  const interval = setInterval(() => void checkOnce(), CHECK_INTERVAL_MS);
  return () => clearInterval(interval);
}
